/**
 * @fileoverview AboutSriLanka.jsx
 * 
 * © 2026 KITH Travels (Pvt) Ltd. All rights reserved.
 * This software is the confidential and proprietary information of KITH Travels.
 * You shall not disclose such Confidential Information and shall use it only in 
 * accordance with the terms of the license agreement you entered into with KITH Travels.
 */
import TopImageBanner from '../components/tours/TopImageBanner'
import { IMG } from '../assets/Assets'
import SEO from '../components/ui/SEO'
import DynamicImage from '../components/ui/DynamicImage'
import { useTranslation } from 'react-i18next'

function AboutSriLanka() {
    const { t } = useTranslation();
    return (
        <div className='flex flex-col w-full min-h-screen pt-24 md:pt-32 pb-20'>
            <SEO
                title="About Sri Lanka"
                description="Explore the rich heritage, stunning landscapes, and vibrant culture of Sri Lanka. From UNESCO World Heritage sites and lush tea plantations to golden beaches and exotic wildlife, discover why this island nation is the ultimate travel destination."
                keywords="about Sri Lanka, Sri Lanka travel guide, Sri Lanka culture, Sri Lanka history, UNESCO heritage Sri Lanka, tea plantations Nuwara Eliya, wildlife Sri Lanka, beaches Sri Lanka"
            />
            {/* Hero Section */}
            <div className='px-[5%] md:px-[10%]'>
                <div className='relative w-full h-[45vh] md:h-[60vh]'>
                    <TopImageBanner title={t('about_sl.hero_title')} />
                </div>
            </div>

            {/* Intro Section */}
            <div className='px-[5%] md:px-[10%] mt-20 grid grid-cols-1 lg:grid-cols-2 gap-16 items-center'>
                <div className='flex flex-col gap-6 text-left'>
                    <span className='text-KITH-primary font-bold tracking-widest uppercase text-xs'>{t('about_sl.intro.subtitle')}</span>
                    <h2 className='text-2xl md:text-4xl font-bold text-gray-800 leading-tight'>
                        {t('about_sl.intro.title_p1')} <span className='text-KITH-primary'>{t('about_sl.intro.title_p2')}</span>
                    </h2>
                    <p className='text-gray-600 text-base md:text-lg leading-relaxed text-justify'>
                        {t('about_sl.intro.p1')}
                    </p>
                    <p className='text-gray-600 text-base md:text-lg leading-relaxed text-justify'>
                        {t('about_sl.intro.p2')}
                    </p>
                </div>
                <div className='w-full aspect-square rounded-[3rem] overflow-hidden shadow-2xl'>
                    <DynamicImage
                        HighImage={IMG.Ayubowan_H}
                        LowImage={IMG.Ayubowan_L}
                        alt="Sri Lankan Hospitality"
                    />
                </div>
            </div>

            {/* Highlights Grid */}
            <div className='px-[5%] md:px-[10%] mt-32'>
                <div className='grid grid-cols-1 md:grid-cols-3 gap-8'>
                    {[
                        { title: t('about_sl.highlights.0.title'), desc: t('about_sl.highlights.0.desc'), icon: "fi fi-br-bank" },
                        { title: t('about_sl.highlights.1.title'), desc: t('about_sl.highlights.1.desc'), icon: "fi fi-br-leaf" },
                        { title: t('about_sl.highlights.2.title'), desc: t('about_sl.highlights.2.desc'), icon: "fi fi-br-sun" }
                    ].map((item, idx) => (
                        <div key={idx} className='bg-white p-10 rounded-[2.5rem] shadow-xl border border-gray-50 flex flex-col gap-6 group hover:border-KITH-primary transition-all duration-300'>
                            <div className='w-14 h-14 bg-gray-50 rounded-2xl flex items-center justify-center text-KITH-primary group-hover:bg-KITH-primary group-hover:text-white transition-all'>
                                <i className={`${item.icon} text-2xl`}></i>
                            </div>
                            <h3 className='text-xl font-bold text-gray-800'>{item.title}</h3>
                            <p className='text-gray-500 text-base leading-relaxed'>{item.desc}</p>
                        </div>
                    ))}
                </div>
            </div>

            {/* Cultural Mosaic Section */}
            <div className='px-[5%] md:px-[10%] mt-32 bg-gray-50 py-20 rounded-[4rem]'>
                <div className='max-w-4xl mx-auto text-center flex flex-col gap-8'>
                    <h2 className='text-3xl md:text-4xl font-bold text-gray-800'>{t('about_sl.mosaic.title')}</h2>
                    <p className='text-gray-600 text-lg leading-relaxed italic'>
                        {t('about_sl.mosaic.desc')}
                    </p>
                    <div className='flex flex-wrap justify-center gap-4'>
                        {[0, 1, 2, 3].map((idx) => (
                            <span key={idx} className='px-6 py-2 bg-white rounded-full text-xs font-bold text-KITH-primary shadow-sm border border-gray-100'>
                                {t(`about_sl.mosaic.tags.${idx}`)}
                            </span>
                        ))}
                    </div>
                </div>
            </div>

            {/* Call to Action */}
            <div className='px-[5%] md:px-[10%] mt-32'>
                <div className='bg-KITH-primary-dark rounded-[3rem] p-12 md:p-20 text-center flex flex-col items-center gap-8 relative overflow-hidden'>
                    <div className='absolute inset-0 bg-white/5 skew-x-12 translate-x-1/2'></div>
                    <h2 className='text-2xl md:text-4xl font-bold text-white z-10'>{t('about_sl.cta.title')}</h2>
                    <p className='text-white/70 max-w-2xl z-10'>
                        {t('about_sl.cta.desc')}
                    </p>
                    <button className='px-12 py-5 bg-white text-KITH-primary-dark font-bold rounded-2xl hover:bg-gray-100 transition-all shadow-2xl transform hover:scale-105 z-10'>
                        {t('about_sl.cta.button')}
                    </button>
                </div>
            </div>
        </div>
    )
}

export default AboutSriLanka
